import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { DateString } from '#/types/TypedDateString';

import { getMonthName } from './dateI18n';

/**
 * @example
 *
 * Given the date 2026-04-07
 *
 * //=> EN (d/M/yyyy)
 * 4/7/2026
 * 4/7/26 (short)
 *
 * //=> ES, PT (M/d/yyyy)
 * 7/4/2026
 * 7/4/26 (short)
 */
export function shortDate(
  date: DateString,
  { locale, yearFormat }: { locale: I18nLocale; yearFormat: 'short' | 'long' },
) {
  const [year, month, day] = date.split('-').map(Number);

  const yearString = String(year);
  const formattedYear =
    yearFormat === 'long'
      ? yearString
      : yearString.substring(yearString.length - 2);

  switch (locale) {
    case I18nLocale.EN:
      return `${month}/${day}/${formattedYear}`;
    case I18nLocale.ES:
    case I18nLocale.PT:
      return `${day}/${month}/${formattedYear}`;
  }
}

/**
 * Formats as 'MMM d, yyyy'
 *
 * @example
 *
 * Given the date 2026-04-07
 *
 * //=> EN
 * April 7, 2026
 *
 * //=> ES
 * April 7, 2026
 *
 * //=> PT
 * April 7, 2026
 */
export function humanDate(
  date: DateString,
  {
    locale,
    monthFormat,
  }: { locale: I18nLocale; monthFormat: 'short' | 'long' },
) {
  const [year, month, day] = date.split('-').map(Number);
  const monthName = getMonthName(month, { locale, format: monthFormat });

  return `${monthName} ${day}, ${year}`;
}

export function humanMonthYear(
  date: DateString,
  {
    locale,
    monthFormat,
  }: { locale: I18nLocale; monthFormat: 'short' | 'long' },
) {
  const [year, month] = date.split('-').map(Number);
  const monthName = getMonthName(month, { locale, format: monthFormat });

  return `${monthName} ${year}`;
}
